import {
  useState, useMemo, useCallback 
} from 'react';
import {
  X, Save, UtensilsCrossed, BookOpen, Package, Search, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMenus } from '@/features/menus';
import { useRecipes } from '@/features/recipes';
import { useCatalogItems } from '@/features/catalog';
import type { Recipe } from '@/features/recipes/types';
import type { Menu } from '@/features/menus/types';
import type { CatalogItem } from '@/features/catalog/types';
import type {
  CreateRequisitionLine, RecipeSelection, MenuSelection,
} from '../../types';
import {
  MenuSelector, RecipeSelector, ManualItemSelector 
} from './selectors';
import { aggregateIngredients } from './aggregation';

type SourceMode = 'menu' | 'recipe' | 'manual';

function getSearchPlaceholder(mode: SourceMode): string {
  switch (mode) {
    case 'menu':
      return 'Search menus...';
    case 'recipe':
      return 'Search recipes...';
    case 'manual':
      return 'Search catalog items...';
  }
}

const VENUES = [
  {
    id: 'venue-main',
    name: 'Main Dining Room' 
  },
  {
    id: 'venue-buffet',
    name: 'Deck 9 Buffet' 
  },
  {
    id: 'venue-pool',
    name: 'Pool Bar' 
  },
  {
    id: 'venue-specialty',
    name: 'Specialty Restaurant' 
  },
];

interface CreateRequisitionModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (data: {
    venueId: string;
    venueName: string;
    neededBy: string;
    priority: 'low' | 'normal' | 'high' | 'urgent';
    notes: string;
    items: CreateRequisitionLine[];
  }) => void;
  readonly isSubmitting?: boolean;
  readonly siteId: string;
}

export function CreateRequisitionModal({
  isOpen, onClose, onSubmit, isSubmitting = false, siteId,
}: CreateRequisitionModalProps) {
  const [venueId, setVenueId] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal');
  const [notes, setNotes] = useState('');
  const [sourceMode, setSourceMode] = useState<SourceMode>('menu');
  const [selectedMenus, setSelectedMenus] = useState<MenuSelection[]>([]);
  const [selectedRecipes, setSelectedRecipes] = useState<RecipeSelection[]>([]);
  const [manualItems, setManualItems] = useState<CreateRequisitionLine[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: menusData, isLoading: menusLoading 
  } = useMenus(siteId, { status: 'active' });
  const {
    data: recipesData, isLoading: recipesLoading 
  } = useRecipes({ filters: { type: 'finished' } });
  const {
    data: catalogData, isLoading: catalogLoading 
  } = useCatalogItems({
    filters: {
      search: searchTerm,
      limit: 50 
    } 
  });

  const menus = menusData?.data ?? [];
  const recipes = recipesData?.data ?? [];
  const catalogItems = catalogData?.data ?? [];

  const filteredMenus = useMemo(() => {
    if (!searchTerm) return menus;
    const term = searchTerm.toLowerCase();
    return menus.filter((m: Menu) => m.name.toLowerCase().includes(term) || m.venueName.toLowerCase().includes(term));
  }, [menus, searchTerm]);

  const filteredRecipes = useMemo(() => {
    if (!searchTerm) return recipes;
    const term = searchTerm.toLowerCase();
    return recipes.filter((r: Recipe) => r.name.toLowerCase().includes(term) || r.category.toLowerCase().includes(term));
  }, [recipes, searchTerm]);

  const aggregatedIngredients = useMemo(
    () => aggregateIngredients(selectedMenus, selectedRecipes, menus, recipes),
    [selectedMenus, selectedRecipes, menus, recipes]
  );

  const requisitionLines = useMemo((): CreateRequisitionLine[] => {
    if (sourceMode === 'manual') return manualItems;
    return aggregatedIngredients.map((agg) => ({
      itemId: agg.itemId,
      itemName: agg.itemName,
      sku: agg.sku,
      quantity: Math.ceil(agg.totalQuantity * 100) / 100,
      unit: agg.unit,
      sourceType: agg.sources[0]?.type,
      sourceId: agg.sources[0]?.id,
      sourceName: agg.sources.map((s) => s.name).join(', '),
    }));
  }, [sourceMode, manualItems, aggregatedIngredients]);

  const handleAddMenu = useCallback((menu: Menu) => {
    setSelectedMenus((prev) => {
      if (prev.some((m) => m.menuId === menu.menuId)) return prev;
      return [...prev, {
        menuId: menu.menuId,
        menuName: menu.name,
        paxCount: menu.defaultPax ?? 50 
      }];
    });
  }, []);

  const handleRemoveMenu = useCallback((menuId: string) => {
    setSelectedMenus((prev) => prev.filter((m) => m.menuId !== menuId));
  }, []);

  const handleUpdateMenuPax = useCallback((menuId: string, paxCount: number) => {
    setSelectedMenus((prev) => prev.map((m) => (m.menuId === menuId ? {
      ...m,
      paxCount: Math.max(1, paxCount) 
    } : m)));
  }, []);

  const handleAddRecipe = useCallback((recipe: Recipe) => {
    setSelectedRecipes((prev) => {
      if (prev.some((r) => r.recipeId === recipe.recipeId)) return prev;
      return [...prev, {
        recipeId: recipe.recipeId,
        recipeName: recipe.name,
        portions: recipe.servings 
      }];
    });
  }, []);

  const handleRemoveRecipe = useCallback((recipeId: string) => {
    setSelectedRecipes((prev) => prev.filter((r) => r.recipeId !== recipeId));
  }, []);

  const handleUpdateRecipePortions = useCallback((recipeId: string, portions: number) => {
    setSelectedRecipes((prev) => prev.map((r) => (r.recipeId === recipeId ? {
      ...r,
      portions: Math.max(1, portions) 
    } : r)));
  }, []);

  const handleAddManualItem = useCallback((item: CatalogItem) => {
    setManualItems((prev) => {
      if (prev.some((i) => i.itemId === item.itemId)) return prev;
      return [...prev, {
        itemId: item.itemId,
        itemName: item.name,
        sku: item.sku,
        quantity: 1,
        unit: item.unit,
        sourceType: 'manual' as const 
      }];
    });
  }, []);

  const handleRemoveManualItem = useCallback((itemId: string) => {
    setManualItems((prev) => prev.filter((i) => i.itemId !== itemId));
  }, []);

  const handleUpdateManualQuantity = useCallback((itemId: string, quantity: number) => {
    setManualItems((prev) => prev.map((i) => (i.itemId === itemId ? {
      ...i,
      quantity: Math.max(0.01, quantity) 
    } : i)));
  }, []);

  const handleSubmit = () => {
    const venue = VENUES.find((v) => v.id === venueId);
    if (!venue || !neededBy || requisitionLines.length === 0) return;
    onSubmit({
      venueId,
      venueName: venue.name,
      neededBy,
      priority,
      notes,
      items: requisitionLines 
    });
  };

  const canSubmit = venueId && neededBy && requisitionLines.length > 0 && !isSubmitting;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg bg-background shadow-xl flex flex-col">
        <div className="flex items-start justify-between p-6 border-b">
          <div>
            <h3 className="text-lg font-semibold">Create Requisition</h3>
            <p className="text-sm text-muted-foreground">Request stock from central storage for your venue</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1 hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Venue <span className="text-red-500">*</span></label>
              <select value={venueId} onChange={(e) => setVenueId(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Select venue...</option>
                {VENUES.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Needed By <span className="text-red-500">*</span></label>
              <input type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div className="border rounded-lg">
            <div className="flex border-b">
              <button type="button" onClick={() => setSourceMode('menu')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${sourceMode === 'menu' ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                <UtensilsCrossed className="h-4 w-4" />From Menu
              </button>
              <button type="button" onClick={() => setSourceMode('recipe')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${sourceMode === 'recipe' ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                <BookOpen className="h-4 w-4" />From Recipes
              </button>
              <button type="button" onClick={() => setSourceMode('manual')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${sourceMode === 'manual' ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                <Package className="h-4 w-4" />Manual Items
              </button>
            </div>
            <div className="p-4">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input type="text" placeholder={getSearchPlaceholder(sourceMode)} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm" />
              </div>
              {sourceMode === 'menu' && <MenuSelector menus={filteredMenus} selectedMenus={selectedMenus} onAdd={handleAddMenu} onRemove={handleRemoveMenu} onUpdatePax={handleUpdateMenuPax} isLoading={menusLoading} />}
              {sourceMode === 'recipe' && <RecipeSelector recipes={filteredRecipes} selectedRecipes={selectedRecipes} onAdd={handleAddRecipe} onRemove={handleRemoveRecipe} onUpdatePortions={handleUpdateRecipePortions} isLoading={recipesLoading} />}
              {sourceMode === 'manual' && <ManualItemSelector items={catalogItems} selectedItems={manualItems} onAdd={handleAddManualItem} onRemove={handleRemoveManualItem} onUpdateQuantity={handleUpdateManualQuantity} isLoading={catalogLoading} />}
            </div>
          </div>
          {requisitionLines.length > 0 && (
            <div className="border rounded-lg p-4">
              <h4 className="font-medium mb-3 flex items-center gap-2"><ChevronRight className="h-4 w-4" />Requisition Items ({requisitionLines.length})</h4>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {requisitionLines.map((line) => (
                  <div key={line.itemId} className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded text-sm">
                    <div>
                      <span className="font-medium">{line.itemName}</span>
                      <span className="text-muted-foreground ml-2">({line.sku})</span>
                      {line.sourceName && <span className="text-xs text-muted-foreground block">From: {line.sourceName}</span>}
                    </div>
                    <div className="font-medium">{line.quantity} {line.unit}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Special instructions or comments..." rows={2} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}><Save className="mr-2 h-4 w-4" />{isSubmitting ? 'Creating...' : 'Create Requisition'}</Button>
        </div>
      </div>
    </div>
  );
}
